import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { AppLayout } from 'components/AppLayout'
import { Button } from 'components/Button'
import {
  formatResultCount,
  ManagementFilterBar,
  ManagementPagination,
  ManagementSearchInput,
  ManagementSelectFilter,
} from 'components/ManagementFilters'
import { pageCardClasses } from 'components/AppLayout/styles'
import { MANAGEMENT_PAGE_SIZE } from 'constants/index'
import { routes } from 'constants/routes'
import { banStatusStrings, commonStrings } from 'constants/strings'
import { useAdminBanStatus, useAdminBanStatusChannels, useCurrentUser, useOrgPath } from 'hooks'
import { BAN_STATUS_CHANNEL_CATEGORIES } from 'types'
import type { TBanStatusChannelCategory } from 'types'
import { BanStatusSummary } from './BanStatusSummary'
import { BlockedOrganizations } from './BlockedOrganizations'
import { OrganizationBreakdownCardList } from './OrganizationBreakdownCardList'
import { OrganizationBreakdownTable } from './OrganizationBreakdownTable'
import { OrganizationBreakdownSort } from './OrganizationBreakdownSort'
import { PausedChannelsCardList } from './PausedChannelsCardList'
import { PausedChannelsTable } from './PausedChannelsTable'
import {
  getCategoryLabel,
  isOrganizationBlocked,
  isAvailableBanStatusCount,
  nextOrganizationSort,
  ORGANIZATION_USER_COUNT_KEYS,
  sortOrganizations,
  type OrganizationSort,
} from './banStatusView'

const categoryFilterOptions = BAN_STATUS_CHANNEL_CATEGORIES.map((category) => ({
  value: category,
  label: getCategoryLabel(category),
}))

export default function BanStatus() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const { data: currentUser } = useCurrentUser()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<
    TBanStatusChannelCategory | ''
  >('')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [page, setPage] = useState(1)
  const [tableSort, setTableSort] = useState<OrganizationSort>(null)
  const [cardSort, setCardSort] = useState<OrganizationSort>(null)

  const {
    banStatus,
    loading: summaryLoading,
    refreshing: summaryRefreshing,
    error: summaryError,
    refresh: refreshSummary,
  } = useAdminBanStatus()
  const {
    channels,
    total,
    totalPage,
    loading: channelsLoading,
    refreshing: channelsRefreshing,
    error: channelsError,
    refresh: refreshChannels,
  } = useAdminBanStatusChannels({
    organizationId: organizationFilter ? Number(organizationFilter) : undefined,
    category: categoryFilter || undefined,
    keyword: searchKeyword,
    currentPage: page,
    pageSize: MANAGEMENT_PAGE_SIZE,
  })

  const organizations = useMemo(
    () => banStatus?.organizations ?? [],
    [banStatus],
  )
  const hasUnavailableUserCounts = organizations.some((organization) =>
    ORGANIZATION_USER_COUNT_KEYS.some((key) => !isAvailableBanStatusCount(organization[key])),
  )

  // 絞り込みを変えたら1ページ目へ戻す
  useEffect(() => {
    setPage(1)
  }, [searchKeyword, categoryFilter, organizationFilter])

  const tableOrganizations = useMemo(
    () => sortOrganizations(organizations, tableSort),
    [organizations, tableSort],
  )
  const cardOrganizations = useMemo(
    () => sortOrganizations(organizations, cardSort),
    [organizations, cardSort],
  )
  // 要対応の警告は、内訳のソート操作にかかわらず従来の優先順を保つ。
  const blockedOrganizations = useMemo(
    () => sortOrganizations(organizations.filter(isOrganizationBlocked)),
    [organizations],
  )
  // 組織一覧は集計クエリから作る。一覧側が失敗しても選択肢は消えない
  const organizationFilterOptions = useMemo(
    () =>
      organizations.map((organization) => ({
        value: String(organization.organizationId),
        label: organization.organizationName,
      })),
    [organizations],
  )

  const isFiltering = Boolean(
    searchKeyword || categoryFilter || organizationFilter,
  )
  const channelsEmptyMessage = isFiltering
    ? commonStrings.filteredEmpty
    : banStatusStrings.channels.empty
  const handleRefresh = () => {
    refreshSummary()
    refreshChannels()
  }
  const handleEdit = (lineChannelId: number) => {
    // LINE 編集は組織スコープ配下。ADMIN 全体画面からは ADMIN の所属組織
    // （単一所属）のスコープで開く
    navigate(
      orgPathFor(routes.LINE_EDIT.replace(':lineChannelId', String(lineChannelId))),
    )
  }

  return (
    <AppLayout>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {banStatusStrings.title}
            </h1>
            <p className="text-xs text-muted">{banStatusStrings.description}</p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={
              summaryLoading || summaryRefreshing ||
              channelsLoading || channelsRefreshing
            }
          >
            {banStatusStrings.actions.refresh}
          </Button>
        </header>

        {summaryError && (
          <div className="mt-6">
            <Alert>{summaryError}</Alert>
          </div>
        )}

        {/* 取得できていないときは「異常なし」と読める表示を出さない */}
        {summaryLoading ? (
          <p className="mt-6 rounded-2xl border border-border/70 bg-white px-4 py-10 text-center text-sm text-muted">
            {commonStrings.loading}
          </p>
        ) : banStatus ? (
          <>
            <BanStatusSummary summary={banStatus.summary} />
            <BlockedOrganizations organizations={blockedOrganizations} />

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-ink">
                {banStatusStrings.organizations.heading}
              </h2>
              <div className="hidden lg:block">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={tableSort === null}
                  onClick={() => setTableSort(null)}
                >
                  {banStatusStrings.organizations.sort.reset}
                </Button>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted">
              {banStatusStrings.organizations.userCountsNote}
            </p>
            {hasUnavailableUserCounts && (
              <p role="status" className="mt-2 text-xs text-muted">
                {banStatusStrings.states.userCountsUnavailable}
              </p>
            )}
            <div className="mt-3 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
              <OrganizationBreakdownTable
                organizations={tableOrganizations}
                loading={false}
                sort={tableSort}
                onSort={(key) =>
                  setTableSort((current) => nextOrganizationSort(current, key))
                }
              />
            </div>
            <div className="mt-3 lg:hidden" data-testid="organization-cards">
              <OrganizationBreakdownSort sort={cardSort} onChange={setCardSort} />
              <OrganizationBreakdownCardList
                organizations={cardOrganizations}
                loading={false}
                className="md:grid-cols-2"
              />
            </div>
          </>
        ) : (
          <p className="mt-6 rounded-2xl border border-border/70 bg-surface/70 px-4 py-10 text-center text-sm text-muted">
            {banStatusStrings.states.unknown}
          </p>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="grid gap-1">
            <h2 className="text-sm font-semibold text-ink">
              {banStatusStrings.channels.heading}
            </h2>
            <p className="text-[11px] text-muted">
              {banStatusStrings.channels.description}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {formatResultCount(channels.length, total)}
            </span>
            <ManagementPagination
              currentPage={page}
              totalPage={totalPage}
              loading={channelsLoading}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() =>
                setPage((current) => Math.min(totalPage, current + 1))
              }
            />
          </div>
        </div>

        {channelsError && (
          <div className="mt-3">
            <Alert>{channelsError}</Alert>
          </div>
        )}

        <ManagementFilterBar>
          <ManagementSearchInput
            label={commonStrings.filters.searchLabel}
            placeholder={banStatusStrings.filters.searchPlaceholder}
            value={searchKeyword}
            onChange={setSearchKeyword}
            onClear={() => setSearchKeyword('')}
          />
          <ManagementSelectFilter
            label={banStatusStrings.filters.status}
            value={categoryFilter}
            options={categoryFilterOptions}
            onChange={setCategoryFilter}
            onClear={() => setCategoryFilter('')}
          />
          <ManagementSelectFilter
            label={banStatusStrings.filters.organization}
            value={organizationFilter}
            options={organizationFilterOptions}
            onChange={setOrganizationFilter}
            onClear={() => setOrganizationFilter('')}
          />
        </ManagementFilterBar>

        {channelsError ? null : (
          <>
            <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
              <PausedChannelsTable
                channels={channels}
                loading={channelsLoading}
                emptyMessage={channelsEmptyMessage}
                currentOrganizationId={currentUser?.organizationId}
                onEdit={handleEdit}
              />
            </div>
            <div className="mt-6 lg:hidden" data-testid="channel-cards">
              <PausedChannelsCardList
                channels={channels}
                loading={channelsLoading}
                emptyMessage={channelsEmptyMessage}
                className="md:grid-cols-2"
                currentOrganizationId={currentUser?.organizationId}
                onEdit={handleEdit}
              />
            </div>
            <p className="mt-2 text-[11px] text-muted">
              {banStatusStrings.channels.updatedAtNote}
            </p>
          </>
        )}
      </section>
    </AppLayout>
  )
}
